import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';
import { COLLECTION_MALLS, type CollectionMall } from '../productCollection/collectionMalls';
import { COLLECTION_FIELD_TYPES, newRowKey, normalizeCollectionConfig, type ProductCollectionConfig } from '../productCollection/types';
import type { ProductCollectionByMall } from '../productCollection/ProductCollectionMallTabs';
import type { CollectionTemplate } from './templateModel';

/**
 * QA-563 추가입력·동의 템플릿 CRUD — catalog `/admin/v1/product-collection-templates`(catalog #103, gateway #25).
 * field = 상품 추가입력 PUT 과 같은 모양({fieldKey?, label, fieldType, required, sensitive, options}), consent 본문 키는
 * contentHtml. 상품 폼과 같이 BO 가 모르는 유형(PHONE·EMAIL·DATE)은 단답형(TEXT)으로 읽는다. fieldKey 는 비우면 상품에 붙일 때 새로 만든다.
 * BE 반영 전(404·501)에는 목록이 null — 화면은 기본 템플릿만 쓰고 저장 버튼을 막는다.
 */

const VALID_TYPES = new Set<string>(COLLECTION_FIELD_TYPES);
const BASE = '/admin/v1/product-collection-templates';

interface WireField {
  label?: string;
  fieldType?: string;
  type?: string;
  required?: boolean;
  sensitive?: boolean;
  options?: string[] | null;
}
interface WireConsent {
  title?: string;
  contentHtml?: string | null;
  content?: string | null;
  linkUrl?: string | null;
  required?: boolean;
}
interface WireMall {
  fields?: WireField[] | null;
  consents?: WireConsent[] | null;
}
interface WireTemplate {
  templateId: string;
  name: string;
  malls?: Partial<Record<CollectionMall, WireMall>> | null;
}

const fromWireMall = (m: WireMall | null | undefined): ProductCollectionConfig => ({
  fields: (m?.fields ?? []).map((f) => ({
    key: newRowKey('field'),
    label: f.label ?? '',
    type: (VALID_TYPES.has(f.fieldType ?? f.type ?? '') ? (f.fieldType ?? f.type) : 'TEXT') as ProductCollectionConfig['fields'][number]['type'],
    required: f.required ?? false,
    options: f.options ?? [],
    sensitive: f.sensitive ?? false,
  })),
  consents: (m?.consents ?? []).map((c) => ({
    key: newRowKey('consent'),
    title: c.title ?? '',
    content: c.contentHtml ?? c.content ?? '',
    linkUrl: c.linkUrl ?? '',
    required: c.required ?? true,
  })),
});

const fromWire = (t: WireTemplate): CollectionTemplate => ({
  templateId: t.templateId,
  name: t.name,
  malls: COLLECTION_MALLS.reduce((acc, m) => {
    acc[m] = fromWireMall(t.malls?.[m]);
    return acc;
  }, {} as ProductCollectionByMall),
});

/** 저장 본문 — 몰마다 빈 행을 정리해 보낸다(클라이언트 key 는 보내지 않는다). */
export const toWire = (name: string, malls: ProductCollectionByMall) => ({
  name: name.trim(),
  malls: COLLECTION_MALLS.reduce(
    (acc, m) => {
      const n = normalizeCollectionConfig(malls[m]);
      acc[m] = {
        fields: n.fields.map((f) => ({ label: f.label, fieldType: f.type, required: f.required, sensitive: f.sensitive, options: f.options })),
        consents: n.consents.map((c) => ({ title: c.title, contentHtml: c.content, linkUrl: c.linkUrl, required: c.required })),
      };
      return acc;
    },
    {} as Record<CollectionMall, unknown>,
  ),
});

export const collectionTemplatesQueryKey = ['admin', 'product-collection-templates'] as const;

/** 저장된 템플릿 목록 — BE 미반영이면 null. */
export const useCollectionTemplatesQuery = (enabled = true) =>
  useQuery<CollectionTemplate[] | null>({
    queryKey: collectionTemplatesQueryKey,
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await adminFetch(BASE);
      if (res.status === 404 || res.status === 501) return null;
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: WireTemplate[] | { items?: WireTemplate[] } | null };
      const list = Array.isArray(env.data) ? env.data : (env.data?.items ?? []);
      return list.map(fromWire);
    },
  });

export const useSaveCollectionTemplateMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, { templateId?: string; name: string; malls: ProductCollectionByMall }>({
    mutationFn: async ({ templateId, name, malls }) => {
      const body = toWire(name, malls);
      const path = templateId ? `${BASE}/${templateId}` : BASE;
      const method = templateId ? 'PUT' : 'POST';
      const res = await adminFetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor(method, path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: collectionTemplatesQueryKey }),
  });
};

export const useDeleteCollectionTemplateMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (templateId) => {
      const path = `${BASE}/${templateId}`;
      const res = await adminFetch(path, { method: 'DELETE', headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, {}) } });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: collectionTemplatesQueryKey }),
  });
};
